import { create } from "zustand";
import { persist, PersistOptions } from "zustand/middleware";

const generateUUID = (): string => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

const getSessionId = (): string => {
    if (typeof window === "undefined") {
        return generateUUID();
    }
    let sessionId = localStorage.getItem("sessionId");
    if (!sessionId) {
        sessionId = generateUUID();
        localStorage.setItem("sessionId", sessionId);
    }
    return sessionId;
};

const getUserId = (): string | null => {
    if (typeof window === "undefined") {
        return null;
    }
    const storageData = localStorage.getItem("auth-storage");
    return storageData ? JSON.parse(storageData)?.state?.user?.id : null;
};

export interface CartItem {
    id: string;
    product_item_id: string | number;
    title: string;
    price: number;
    quantity: number;
    seller_title: string;
    slug: string;
    discount_percentage: string | number;
    sale_price: string | number;
    image: string | null;
    unique_id: string | null;
    active: boolean | number;
    max_basket_limit: number;
    min_total_qty_and_max_basket_qty: number;
}

interface CartStore {
    card_status: "current" | "next";
    items: CartItem[];
    addItem: (item: CartItem) => void;
    removeItem: (id: string | null | undefined) => void;
    updateQuantity: (id: string | null, quantity: number) => void;
    updateStatus: (id: string | null, active: boolean) => void;
    changeCardStatus: (status: "current" | "next") => void;
    clearCart: () => void;
    clearActiveItems: (items: CartItem[]) => void;
    updateEntireCart: (newItems: CartItem[]) => void;
    replaceEntireCart: (newItems: CartItem[]) => void;
}

const persistOptions: PersistOptions<CartStore> = {
    name: `cart-storage-${typeof window === "undefined" ? generateUUID() : (getUserId() || getSessionId())}`,
    partialize: (state) => ({
        items: state.items,
        card_status: state.card_status,
    }) as any,
    storage: typeof window !== "undefined" ? localStorage : undefined as any,
};

export const useCartStore = create<CartStore>()(
    persist(
        (set) => ({
            card_status: "current",
            items: [],
            addItem: (item) =>
                set((state) => {
                    const existingItem = state.items.find((i) => i.unique_id === item.unique_id);
                    if (existingItem) {
                        return {
                            items: state.items.map((i) =>
                                i.unique_id === item.unique_id ? { ...i, quantity: i.quantity + 1 } : i
                            ),
                        };
                    }
                    return { items: [...state.items, item] };
                }),
            removeItem: (unique_id) =>
                set((state) => ({
                    items: state.items.filter((i) => i.unique_id !== unique_id),
                })),
            updateQuantity: (unique_id, quantity) =>
                set((state) => ({
                    items: state.items.map((i) => (i.unique_id === unique_id ? { ...i, quantity } : i)),
                })),
            updateStatus: (unique_id, active) =>
                set((state) => ({
                    items: state.items.map((i) => (i.unique_id === unique_id ? { ...i, active } : i)),
                })),
            changeCardStatus: (status: "current" | "next") =>
                set(() => ({
                    card_status: status,
                })),
            clearCart: () => set({ items: [] }),
            clearActiveItems: (items: CartItem[]) =>
                set({ items: items.filter((item: CartItem) => item.active === 1) }),
            replaceEntireCart: (newItems) =>
                set({
                    items: newItems.map((i: any) => ({ ...i })),
                }),
            updateEntireCart: (newItems) =>
                set((state) => {
                    if (JSON.stringify(newItems) !== JSON.stringify(state.items)) {
                        return { items: newItems };
                    }
                    return state;
                }),
        }),
        persistOptions
    )
);